import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { apiService } from "../services/api";

// Same base64 -> Blob -> anchor pattern as useBulkExportCommissions, with a
// PDF MIME type. The BE returns the PDF inside the JSON payload.
const downloadPdfBase64 = (pdfBase64: string, fileName: string) => {
  const byteCharacters = atob(pdfBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useMonthlyReports = () => {
  return useQuery({
    queryKey: ["monthly-reports"],
    queryFn: () => apiService.getMonthlyReports(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useGenerateMonthlyReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      apiService.generateMonthlyReport(year, month),
    onSuccess: (data) => {
      if (data.pdfBase64) {
        downloadPdfBase64(data.pdfBase64, data.fileName);
      }
      toast.success(
        `Report generated: ${data.report.totalCount} payments, €${data.report.totalNet.toFixed(2)} net`,
      );
      queryClient.invalidateQueries({ queryKey: ["monthly-reports"] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to generate monthly report",
      );
    },
  });
};

export const useDownloadMonthlyReport = () => {
  return useMutation({
    mutationFn: (id: string) => apiService.downloadMonthlyReport(id),
    onSuccess: (data) => {
      downloadPdfBase64(data.pdfBase64, data.fileName);
      toast.success(`Downloaded ${data.fileName}`);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to download monthly report",
      );
    },
  });
};
